import React, { useCallback, useEffect, useState } from 'react';
import Form, { InputField } from '@components/form';
import * as yup from 'yup';
import styles from "../admin/styles.module.scss";
import { Flex } from '@components/ui/Flex';
import { Text } from '@components/ui/Text';
import { ShowWhenTrue } from '@helpers/showwhentrue';
import { Col, Input, Row, Select } from 'antd';
// import { Attribute } from './asset_heirarchy/types';
import { CloseTabIcon } from '@pages/workflow-page/assets/icons';
import { ProjectData } from './asset_heirarchy/types';
import { isEmpty } from 'lodash';

const projectFormSchema = () =>
    yup.object().shape({
        name: yup.string().required('Class Name is required'),
        // nodeType: yup.string().required('Node Type is required'),
        superclass: yup.string(),
        description: yup.string()
    });

export type ProjectFormValues = yup.InferType<typeof projectFormSchema>;

export type ProjectFormProps = {
    initialValues: any;
    submitButtons?: React.ReactNode;
    onSubmit?: (arg0: any) => any;
    schemaCheck: boolean;
    newClasses?: ProjectData[];
}

type CategoryValue = {
    id: number;
    name: string;
};

type Category = {
    id: number;
    name: string;
    values: CategoryValue[];
};

type IndexValue = {
    id: number;
    name: string;
};

type Index = {
    id: number;
    name: string;
    values: IndexValue[];
};

type Attribute = {
    id: number|null;
    name: string;
    dataType: string;
    attributeType: 'Static' | 'Dynamic';
    // Static attribute fields
    valueType?: 'SingleValue' | 'CategoricalValues';
    list?: boolean;
    categories?: Category[];
    // Dynamic attribute fields
    indexed?: boolean;
    indexes?: Index[];
    dynamicValue?: string;
    // Legacy field
    referencedEntityName?: string;
    categorical?: boolean;
};

export const ProjectForm: React.FC<ProjectFormProps> = ({ onSubmit, initialValues, submitButtons, schemaCheck, newClasses = [] }) => {
    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const handleAddAttribute = () => {
        setAttributes(prev => [
            ...prev,
            {
                id: null,
                name: '',
                dataType: 'String',
                attributeType: 'Static',
                valueType: 'SingleValue',
                categorical: false,
                list: false,
                categories: [],
                indexed: false,
                indexes: [],
                dynamicValue: '',
                referencedEntityName: ''
            }
        ]);
    };

    const handleAttributeChange = (index: number, field: keyof Attribute, value: any) => {
        const updated = [...attributes];
        updated[index] = { ...updated[index], [field]: value };

        // Reset conditional fields when parent field changes
        if (field === 'attributeType') {
            if (value === 'Static') {
                updated[index].valueType = 'SingleValue';
                updated[index].categorical = false;
                updated[index].list = false;
                updated[index].categories = [];
                updated[index].indexed = false;
                updated[index].indexes = [];
                updated[index].dynamicValue = '';
            } else if (value === 'Dynamic') {
                updated[index].valueType = undefined;
                updated[index].list = false;
                updated[index].categories = [];
                updated[index].indexed = false;
                updated[index].indexes = [];
                updated[index].dynamicValue = '';
            }
        }

        if (field === 'valueType') {
            if (value === 'SingleValue') {
                updated[index].categories = [];
            } else if (value === 'CategoricalValues') {
                updated[index].list = false;
            }
        }

        if (field === 'indexed') {
            if (!value) {
                updated[index].indexes = [];
            }
        }

        setAttributes(updated);
    };

    // Category handling functions
    const handleAddCategory = (attrIndex: number) => {
        const updated = [...attributes];
        const categories = updated[attrIndex].categories || [];
        updated[attrIndex].categories = [
            ...categories,
            {
                id: categories.length + 1,
                name: '',
                values: []
            }
        ];
        setAttributes(updated);
    };

    const handleCategoryChange = (attrIndex: number, catIndex: number, value: string) => {
        const updated = [...attributes];
        if (updated[attrIndex].categories) {
            updated[attrIndex].categories![catIndex].name = value;
            setAttributes(updated);
        }
    };

    const handleDeleteCategory = (attrIndex: number, catIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].categories) {
            updated[attrIndex].categories!.splice(catIndex, 1);
            setAttributes(updated);
        }
    };

    // Category value handling functions
    const handleAddCategoryValue = (attrIndex: number, catIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].categories) {
            const values = updated[attrIndex].categories![catIndex].values;
            updated[attrIndex].categories![catIndex].values = [
                ...values,
                {
                    id: values.length + 1,
                    name: ''
                }
            ];
            setAttributes(updated);
        }
    };

    const handleCategoryValueChange = (attrIndex: number, catIndex: number, valueIndex: number, value: string) => {
        const updated = [...attributes];
        if (updated[attrIndex].categories) {
            updated[attrIndex].categories![catIndex].values[valueIndex].name = value;
            setAttributes(updated);
        }
    };

    const handleDeleteCategoryValue = (attrIndex: number, catIndex: number, valueIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].categories) {
            updated[attrIndex].categories![catIndex].values.splice(valueIndex, 1);
            setAttributes(updated);
        }
    };

    // Index handling functions
    const handleAddIndex = (attrIndex: number) => {
        const updated = [...attributes];
        const indexes = updated[attrIndex].indexes || [];
        updated[attrIndex].indexes = [
            ...indexes,
            {
                id: indexes.length + 1,
                name: '',
                values: []
            }
        ];
        setAttributes(updated);
    };

    const handleIndexChange = (attrIndex: number, indexIndex: number, value: string) => {
        const updated = [...attributes];
        if (updated[attrIndex].indexes) {
            updated[attrIndex].indexes![indexIndex].name = value;
            setAttributes(updated);
        }
    };

    const handleDeleteIndex = (attrIndex: number, indexIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].indexes) {
            updated[attrIndex].indexes!.splice(indexIndex, 1);
            setAttributes(updated);
        }
    };

    // Index value handling functions
    const handleAddIndexValue = (attrIndex: number, indexIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].indexes) {
            const values = updated[attrIndex].indexes![indexIndex].values;
            updated[attrIndex].indexes![indexIndex].values = [
                ...values,
                {
                    id: values.length + 1,
                    name: ''
                }
            ];
            setAttributes(updated);
        }
    };

    const handleIndexValueChange = (attrIndex: number, indexIndex: number, valueIndex: number, value: string) => {
        const updated = [...attributes];
        if (updated[attrIndex].indexes) {
            updated[attrIndex].indexes![indexIndex].values[valueIndex].name = value;
            setAttributes(updated);
        }
    };

    const handleDeleteIndexValue = (attrIndex: number, indexIndex: number, valueIndex: number) => {
        const updated = [...attributes];
        if (updated[attrIndex].indexes) {
            updated[attrIndex].indexes![indexIndex].values.splice(valueIndex, 1);
            setAttributes(updated);
        }
    };

    const handleDeleteAttribute = (index: number) => {
        const updated = [...attributes];
        updated.splice(index, 1);
        setAttributes(updated);
    };

    const handleFormSubmit = useCallback((values: any) => {
        if (onSubmit) {
            return onSubmit({ ...values, attributes });
        }
    }, [onSubmit, attributes]);

    useEffect(() => {
        if (!isEmpty(initialValues?.attributes)) {
            setAttributes(initialValues?.attributes.map((item: any) => {
                return {
                    name: item.name,
                    attributeType: item.attributeType || "Static",
                    dataType: item.dataType || "String",
                    referencedEntityName: item.referencedEntityName || "",
                    valueType: item.valueType || "SingleValue",
                    list: item.list || false,
                    categorical: item.categorical || false,
                    categories: item.categories || [],
                    indexed: item.indexed || false,
                    indexes: item.indexes || [],
                    dynamicValue: item.dynamicValue || "",
                    id: item.id,
                    superclass:item.superclass,
                    // isRequired:item.isRequired,
                    // validation:item.validation,
                    value: item.value
                }
            }))
        }
    }, [initialValues])

    return (
        <Form
            initialValues={initialValues}
            validationSchema={projectFormSchema()}
            onSubmit={handleFormSubmit as any}
            enableReinitialize
        >
            {({ _formikprops: { errors, setFieldValue, touched, values } }) => {
                return (
                    <>
                        <Flex flexDirection="column" gap="16px">
                            <div className="_form--halfwide">
                                <InputField
                                    name="name"
                                    label="Class Name"
                                    required
                                    autoComplete="off"
                                />

                                <InputField
                                    name="description"
                                    label="Description"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="_form--halfwide">
                                <div>
                                    <span className={'red-star'} >Node Type</span>
                                    <Select
                                        style={{ width: '100%', marginTop: 5 }}
                                        placeholder="Node Type"
                                        value={values.nodeType}
                                        onChange={(val) => setFieldValue('nodeType', val)}
                                    >
                                        <Select.Option value="Entity Class">Entity Class</Select.Option>
                                        <Select.Option value="Abstract Class">Abstract Class</Select.Option>
                                    </Select>
                                </div>
                                <div>
                                    <ShowWhenTrue show={schemaCheck}>

                                        <span>Super Class </span>
                                        <Select
                                            style={{ width: '100%', marginTop: 5 }}
                                            placeholder="Super Class"
                                            value={values.superclass}
                                            onChange={(val) => setFieldValue('superclass', val)}
                                            notFoundContent={<div style={{ padding: '10px', color: 'white', textAlign: 'center', backgroundColor: '#2A2C42' }}>No data present</div>}
                                        >
                                            {newClasses?.map((item, index) => (
                                                <Select.Option key={index} value={item.name}>{item.name}</Select.Option>
                                            ))}
                                        </Select>
                                    </ShowWhenTrue>
                                </div>
                            </div>

                            <div className={styles['divider']}></div>

                            <Flex justifyContent="space-between" alignItems="center">
                                <Text color='white'>Attributes</Text>
                                <button
                                    className="btn-sm btn-yellow-transparent btn__new__action"
                                    type="button"
                                    onClick={handleAddAttribute}
                                >
                                    <img src="/icons/workflow/add.svg" alt="" /> Attribute
                                </button>
                            </Flex>

                            <div className={styles['attributes-project-lists']}>
                                {attributes.map((attr, i) => (
                                    <div key={i} className={styles['attributes-row']}>
                                        {/* Row 1: Name, Datatype, and Attribute Type */}
                                        <Row gutter={16} className={styles['attributes-row']} style={{ marginBottom: '10px' }}>
                                            <Col span={6}>
                                                <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Name</Text>
                                                <Input
                                                    value={attr.name}
                                                    placeholder="Name"
                                                    onChange={e =>
                                                        handleAttributeChange(i, 'name', e.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col span={6}>
                                                <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Attribute Type</Text>
                                                <Select
                                                    value={attr.attributeType}
                                                    onChange={val =>
                                                        handleAttributeChange(i, 'attributeType', val)
                                                    }
                                                    style={{ width: '100%' }}
                                                >
                                                    <Select.Option value="Static">Static</Select.Option>
                                                    <Select.Option value="Dynamic">Dynamic</Select.Option>
                                                </Select>
                                            </Col>
                                            <Col span={6}>
                                                <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Data Type</Text>
                                                <Select
                                                    value={attr.dataType}
                                                    onChange={val => {
                                                        handleAttributeChange(i, 'dataType', val)
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Select.Option value="String">String</Select.Option>
                                                    <Select.Option value="Float">Float</Select.Option>
                                                    <Select.Option value="Integer">Integer</Select.Option>
                                                    <Select.Option value="Date">Date</Select.Option>
                                                    <Select.Option value="Boolean">Boolean</Select.Option>
                                                    {/* <Select.Option value="Dropdown">Dropdown</Select.Option> */}
                                                    {schemaCheck && <Select.Option value="Entity">Entity</Select.Option>}
                                                </Select>
                                            </Col>
                                            <Col span={5}>
                                                <ShowWhenTrue show={schemaCheck && attr.dataType === 'Entity'}>
                                                    {/* <Row gutter={16} style={{ marginLeft: '20px', marginTop: '10px', marginBottom: '15px' }}> */}
                                                    {/* <Col span={18}> */}
                                                    <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Entity Class</Text>
                                                    <Select
                                                        // style={{ width: '100%' }}
                                                        placeholder="Select Entity Class"
                                                        value={attr.referencedEntityName}
                                                        onChange={(val) => handleAttributeChange(i, 'referencedEntityName', val)}
                                                    >
                                                        {newClasses?.map((item, index) => (
                                                            <Select.Option key={index} value={item.name}>{item.name}</Select.Option>
                                                        ))}
                                                    </Select>
                                                    {/* </Col> */}
                                                    {/* </Row> */}
                                                </ShowWhenTrue>
                                            </Col>
                                            <Col span={1} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteAttribute(i)}
                                                    style={{ cursor: 'pointer', background: 'red', padding: 4, borderRadius: 4 }}
                                                >
                                                    <CloseTabIcon />
                                                </button>
                                            </Col>
                                        </Row>

                                        {/* Static Attribute Fields */}
                                        {attr.attributeType === 'Static' && (
                                            <div style={{ marginLeft: '12px' }}>
                                                {/* <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                    <Col span={12}>
                                                        <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Value Type</Text>
                                                        <Select
                                                            value={attr.valueType}
                                                            onChange={val =>
                                                                handleAttributeChange(i, 'valueType', val)
                                                            }
                                                            style={{ width: '100%' }}
                                                        >
                                                            <Select.Option value="SingleValue">Single Value</Select.Option>
                                                            <Select.Option value="CategoricalValues">Categorical Values</Select.Option>
                                                        </Select>
                                                    </Col>
                                                </Row> */}
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Flex alignItems="center">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ color: 'white', marginRight: '8px' }}>Categorical</label>
                                                                <input
                                                                    type="radio"
                                                                    id={`categorical-${i}-yes`}
                                                                    name={`categorical-${i}-yes`}
                                                                    checked={attr.categorical}
                                                                    onChange={() => handleAttributeChange(i, 'categorical', true)}
                                                                    style={{ marginRight: '4px', accentColor: "#fcad01" }}
                                                                />
                                                                <label htmlFor={`categorical-${i}-yes`} style={{ color: 'white' }}>Yes</label>
                                                                <input
                                                                    type="radio"
                                                                    id={`categorical-${i}-no`}
                                                                    name={`categorical-${i}-no`}
                                                                    checked={!attr.categorical}
                                                                    onChange={() => handleAttributeChange(i, 'categorical', false)}
                                                                    style={{ marginLeft: '8px', marginRight: '4px', accentColor: "#fcad01" }}
                                                                />
                                                                <label htmlFor={`categorical-${i}-no`} style={{ color: 'white' }}>No</label>
                                                            </div>
                                                        </Flex>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Flex alignItems="center">
                                                            <input
                                                                type="checkbox"
                                                                id={`list-${i}`}
                                                                checked={attr.list}
                                                                onChange={e => handleAttributeChange(i, 'list', e.target.checked)}
                                                                style={{ marginRight: '8px', accentColor: "#fcad01" }}
                                                            />
                                                            <label htmlFor={`list-${i}`} style={{ color: 'white' }}>List</label>
                                                        </Flex>
                                                    </Col>
                                                </Row>

                                                {/* <Row gutter={16}>
                                                        <Col span={12}>
                                                            <Flex alignItems="center">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`list-${i}`}
                                                                    checked={attr.list}
                                                                    onChange={e => handleAttributeChange(i, 'list', e.target.checked)}
                                                                    style={{ marginRight: '8px',accentColor: "#fcad01" }}
                                                                />
                                                                <label htmlFor={`list-${i}`} style={{ color: 'white' }}>List</label>
                                                            </Flex>
                                                        </Col>
                                                    </Row> */}

                                                {/* Single Value option */}
                                                {/* {attr.valueType === 'SingleValue' && (
                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <Flex alignItems="center">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <label style={{ color: 'white', marginRight: '8px' }}>List</label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`list-${i}-yes`}
                                                                        name={`list-${i}`}
                                                                        checked={attr.list}
                                                                        onChange={() => handleAttributeChange(i, 'list', true)}
                                                                        style={{ marginRight: '4px', accentColor: "#fcad01" }}
                                                                    />
                                                                    <label htmlFor={`list-${i}-yes`} style={{ color: 'white' }}>Yes</label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`list-${i}-no`}
                                                                        name={`list-${i}`}
                                                                        checked={!attr.list}
                                                                        onChange={() => handleAttributeChange(i, 'list', false)}
                                                                        style={{ marginLeft: '8px', marginRight: '4px', accentColor: "#fcad01" }}
                                                                    />
                                                                    <label htmlFor={`list-${i}-no`} style={{ color: 'white' }}>No</label>
                                                                </div>
                                                            </Flex>
                                                        </Col>
                                                    </Row>
                                                )} */}

                                                {/* Categorical Values option */}
                                                {attr?.categorical && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                            <Col span={24}>
                                                                <Flex justifyContent="space-between" alignItems="center">
                                                                    <Text size='sm' color='white'>Categories</Text>
                                                                    <button
                                                                        className="btn-sm btn-yellow-transparent"
                                                                        type="button"
                                                                        onClick={() => handleAddCategory(i)}
                                                                        style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                    >
                                                                        Add Category
                                                                    </button>
                                                                </Flex>
                                                            </Col>
                                                        </Row>

                                                        {/* Categories list */}
                                                        {attr?.categories && attr?.categories.map((category, catIndex) => (
                                                            <div key={catIndex} style={{ marginLeft: '20px', marginBottom: '15px', border: '1px solid #3A3F44', padding: '10px', borderRadius: '4px' }}>
                                                                <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                                    <Col span={18}>
                                                                        <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Category Name</Text>
                                                                        <Input
                                                                            value={category.name}
                                                                            placeholder="Category Name"
                                                                            onChange={e => handleCategoryChange(i, catIndex, e.target.value)}
                                                                        />
                                                                    </Col>
                                                                    <Col span={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteCategory(i, catIndex)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <CloseTabIcon />
                                                                        </button>
                                                                    </Col>
                                                                </Row>

                                                                {/* <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                                    <Col span={24}>
                                                                        <Flex justifyContent="space-between" alignItems="center">
                                                                            <Text size='sm' color='white'>Values</Text>
                                                                            <button
                                                                                className="btn-sm btn-yellow-transparent"
                                                                                type="button"
                                                                                onClick={() => handleAddCategoryValue(i, catIndex)}
                                                                                style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                            >
                                                                                Add Value
                                                                            </button>
                                                                        </Flex>
                                                                    </Col>
                                                                </Row> */}

                                                                {/* Category values list */}
                                                                {/* {category.values.map((value, valueIndex) => (
                                                                    <Row key={valueIndex} gutter={16} style={{ marginBottom: '5px', marginLeft: '20px' }}>
                                                                        <Col span={18}>
                                                                            <Input
                                                                                value={value.name}
                                                                                placeholder="Value"
                                                                                onChange={e => handleCategoryValueChange(i, catIndex, valueIndex, e.target.value)}
                                                                            />
                                                                        </Col>
                                                                        <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeleteCategoryValue(i, catIndex, valueIndex)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                <CloseTabIcon />
                                                                            </button>
                                                                        </Col>
                                                                    </Row>
                                                                ))} */}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Dynamic Attribute Fields */}
                                        {attr.attributeType === 'Dynamic' && (
                                            <div style={{ marginLeft: '12px', marginTop: '10px', marginBottom: '15px' }}>
                                                <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                    <Col span={12}>
                                                        <Flex alignItems="center">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ color: 'white', marginRight: '8px' }}>Indexed</label>
                                                                <input
                                                                    type="radio"
                                                                    id={`indexed-${i}-yes`}
                                                                    name={`indexed-${i}`}
                                                                    checked={attr.indexed}
                                                                    onChange={() => handleAttributeChange(i, 'indexed', true)}
                                                                    style={{ marginRight: '4px', accentColor: '#fcad01' }}
                                                                />
                                                                <label htmlFor={`indexed-${i}-yes`} style={{ color: 'white' }}>Yes</label>
                                                                <input
                                                                    type="radio"
                                                                    id={`indexed-${i}-no`}
                                                                    name={`indexed-${i}`}
                                                                    checked={!attr.indexed}
                                                                    onChange={() => handleAttributeChange(i, 'indexed', false)}
                                                                    style={{ marginLeft: '8px', marginRight: '4px', accentColor: "#fcad01" }}
                                                                />
                                                                <label htmlFor={`indexed-${i}-no`} style={{ color: 'white' }}>No</label>
                                                            </div>
                                                        </Flex>
                                                    </Col>
                                                </Row>

                                                {/* Non-indexed dynamic attribute */}
                                                {/* {!attr.indexed && (
                                                    <Row gutter={16}>
                                                        <Col span={18}>
                                                            <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Value</Text>
                                                            <Input
                                                                value={attr.dynamicValue}
                                                                placeholder="Dynamic Value"
                                                                onChange={e => handleAttributeChange(i, 'dynamicValue', e.target.value)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                )} */}

                                                {/* Indexed dynamic attribute */}
                                                {attr.indexed && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                            <Col span={24}>
                                                                <Flex justifyContent="space-between" alignItems="center">
                                                                    <Text size='sm' color='white'>Indexes</Text>
                                                                    <button
                                                                        className="btn-sm btn-yellow-transparent"
                                                                        type="button"
                                                                        onClick={() => handleAddIndex(i)}
                                                                        style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                    >
                                                                        Add Index
                                                                    </button>
                                                                </Flex>
                                                            </Col>
                                                        </Row>

                                                        {/* Indexes list */}
                                                        {attr.indexes && attr?.indexes.map((index, indexIndex) => (
                                                            <div key={indexIndex} style={{ marginLeft: '12px', marginBottom: '15px', border: '1px solid #3A3F44', padding: '10px', borderRadius: '4px' }}>
                                                                <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                                    <Col span={18}>
                                                                        <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Index Name</Text>
                                                                        <Input
                                                                            value={index.name}
                                                                            placeholder="Index Name"
                                                                            onChange={e => handleIndexChange(i, indexIndex, e.target.value)}
                                                                        />
                                                                    </Col>
                                                                    <Col span={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteIndex(i, indexIndex)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <CloseTabIcon />
                                                                        </button>
                                                                    </Col>
                                                                </Row>

                                                                {/* <Row gutter={16} style={{ marginBottom: '10px' }}>
                                                                    <Col span={24}>
                                                                        <Flex justifyContent="space-between" alignItems="center">
                                                                            <Text size='sm' color='white'>Values</Text>
                                                                            <button
                                                                                className="btn-sm btn-yellow-transparent"
                                                                                type="button"
                                                                                onClick={() => handleAddIndexValue(i, indexIndex)}
                                                                                style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                            >
                                                                                Add Value
                                                                            </button>
                                                                        </Flex>
                                                                    </Col>
                                                                </Row> */}

                                                                {/* Index values list */}
                                                                {/* {index.values.map((value, valueIndex) => (
                                                                    <Row key={valueIndex} gutter={16} style={{ marginBottom: '5px', marginLeft: '20px' }}>
                                                                        <Col span={18}>
                                                                            <Input
                                                                                value={value.name}
                                                                                placeholder="Value"
                                                                                onChange={e => handleIndexValueChange(i, indexIndex, valueIndex, e.target.value)}
                                                                            />
                                                                        </Col>
                                                                        <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeleteIndexValue(i, indexIndex, valueIndex)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                <CloseTabIcon />
                                                                            </button>
                                                                        </Col>
                                                                    </Row>
                                                                ))} */}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Legacy dropdown field support */}
                                        {attr.dataType === 'Dropdown' && (
                                            <Row gutter={16} style={{ marginLeft: '20px', marginTop: '10px', marginBottom: '15px' }}>
                                                <Col span={18}>
                                                    <Text size='sm' color='white' style={{ marginBottom: '5px' }}>Dropdown Value</Text>
                                                    <Input
                                                        value={attr.referencedEntityName}
                                                        placeholder="Value"
                                                        onChange={e =>
                                                            handleAttributeChange(i, 'referencedEntityName', e.target.value)
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        )}

                                        <div className={styles['divider']} style={{ margin: '20px 0' }}></div>
                                    </div>
                                ))}
                            </div>
                        </Flex>

                        {!!submitButtons && submitButtons}
                    </>
                );
            }}
        </Form>
    );
}